import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { NABadge } from '../NABadge';
import AIInsightPanel from '../AIInsightPanel';
import { FormulaTooltip } from '../FormulaTooltip';
import { Progress } from '../ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, 
  PieChart, Pie, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Treemap
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  DollarSign, 
  Target,
  Repeat,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Brain,
  RefreshCw,
  ChevronRight,
  PieChart as PieChartIcon,
  Wallet,
  BarChart3,
  Activity,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Info,
  Crown,
  Store,
  Calendar,
  Filter,
  Download,
  Zap,
  Shield,
  Clock,
  MapPin,
  Layers,
  Eye,
  ChevronDown,
  Gem,
  ZoomIn
} from 'lucide-react';
import axios from 'axios';
import DrillDownModal from '../DrillDownModal';
import ChartZoomModal, { ChartZoomButton } from '../ChartZoomModal';
import DeepAIInsights from '../DeepAIInsights';
import { formatNumber, formatCurrency, formatAxisValue } from '../../lib/formatters';
import { ExportButton } from '../ExportUtils';
import { AskAIButton } from '../AskAIButton';

import { API_BASE_URL as API } from '../../utils/apiConfig';

// Generate trend data for charts (helper for live data)
const generateTrendData = (totalValue) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonth = new Date().getMonth();
  const last12Months = [];
  
  for (let i = 11; i >= 0; i--) {
    const monthIndex = (currentMonth - i + 12) % 12;
    // Create realistic variation around the average
    const variance = 0.8 + Math.random() * 0.4; // 80% to 120%
    const monthlyValue = (totalValue / 12) * variance;
    last12Months.push({
      month: months[monthIndex],
      value: Math.round(monthlyValue),
      growth: Math.round((variance - 1) * 100)
    });
  }
  return last12Months;
};

// Delta Badge Component with arrow
const DeltaBadge = ({ value, label }) => {
  const isPositive = value >= 0;
  return (
    <div className={`flex items-center gap-1 text-xs ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
      {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      <span>{isPositive ? '+' : ''}{value}%</span>
      {label && <span className="text-muted-foreground ml-1">{label}</span>}
    </div>
  );
};

// Animated KPI Value
const AnimatedValue = ({ value, duration = 800 }) => {
  const [displayValue, setDisplayValue] = useState(value);
  
  useEffect(() => {
    const numValue = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (isNaN(numValue)) return;
    
    let start = 0;
    const increment = numValue / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= numValue) {
        setDisplayValue(numValue);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [value, duration]);
  
  return <span>{typeof displayValue === 'number' ? displayValue.toLocaleString() : displayValue}</span>;
};

// Status indicator
const StatusIndicator = ({ status }) => {
  const config = {
    healthy: { color: 'bg-emerald-500', label: 'On Track' },
    warning: { color: 'bg-amber-500', label: 'Needs Attention' },
    critical: { color: 'bg-red-500', label: 'Critical' }
  };
  const { color } = config[status] || config.healthy;
  return <div className={`w-2 h-2 rounded-full ${color}`} />;
};

// Insight Card Component
const InsightCard = ({ insight, onClick }) => {
  const severityConfig = {
    success: { bg: 'from-emerald-500/10 to-teal-500/5', border: 'border-emerald-500/20', icon: CheckCircle, iconColor: 'text-emerald-400' },
    warning: { bg: 'from-amber-500/10 to-orange-500/5', border: 'border-amber-500/20', icon: AlertTriangle, iconColor: 'text-amber-400' },
    critical: { bg: 'from-red-500/10 to-pink-500/5', border: 'border-red-500/20', icon: AlertCircle, iconColor: 'text-red-400' },
    info: { bg: 'from-blue-500/10 to-cyan-500/5', border: 'border-blue-500/20', icon: Info, iconColor: 'text-blue-400' }
  };
  
  const config = severityConfig[insight.severity] || severityConfig.info;
  const Icon = config.icon;
  
  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all bg-gradient-to-br ${config.bg} ${config.border}`}
      onClick={() => onClick && onClick(insight)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-background/50`}>
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-sm truncate">{insight.title}</h4>
              {insight.priority === 'high' && (
                <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30">HIGH</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">{insight.insight}</p>
            {insight.impact && (
              <div className="mt-2 flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">{insight.impact}</Badge>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Comparison Bar - Your Performance vs Industry Benchmark
const ComparisonBar = ({ label, currentValue, benchmarkValue, vsBenchmark, format = 'currency', benchmarkLabel = 'Industry Avg' }) => {
  const maxValue = Math.max(currentValue, benchmarkValue);
  const currentWidth = (currentValue / maxValue) * 100;
  const benchmarkWidth = (benchmarkValue / maxValue) * 100;
  const isPositive = vsBenchmark >= 0;
  
  const formatValue = (val) => {
    if (format === 'currency') return formatCurrency(val);
    if (format === 'decimal') return val?.toFixed(2) || '0';
    return formatNumber(val);
  };
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <Badge variant="outline" className={`${isPositive ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30'}`}>
          {isPositive ? '+' : ''}{vsBenchmark}%
        </Badge>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-24">Your Performance</span>
          <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full flex items-center justify-end pr-2"
              style={{ width: `${currentWidth}%` }}
            >
              <span className="text-xs text-white font-medium">{formatValue(currentValue)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-24">{benchmarkLabel}</span>
          <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-slate-400 to-slate-500 rounded-full flex items-center justify-end pr-2"
              style={{ width: `${benchmarkWidth}%` }}
            >
              <span className="text-xs text-white font-medium">{formatValue(benchmarkValue)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Revenue At Risk Card
const RiskCard = ({ bucket, revenue, customers, riskLevel }) => {
  const config = {
    medium: { bg: 'from-amber-500/20 to-orange-500/10', border: 'border-amber-500/30', icon: Clock },
    high: { bg: 'from-orange-500/20 to-red-500/10', border: 'border-orange-500/30', icon: AlertTriangle },
    critical: { bg: 'from-red-500/20 to-pink-500/10', border: 'border-red-500/30', icon: AlertCircle }
  };
  const c = config[riskLevel] || config.medium;
  const Icon = c.icon;
  
  // Use imported formatters
  
  return (
    <Card className={`bg-gradient-to-br ${c.bg} ${c.border}`}>
      <CardContent className="p-4 text-center">
        <Icon className={`w-6 h-6 mx-auto mb-2 ${riskLevel === 'critical' ? 'text-red-400' : riskLevel === 'high' ? 'text-orange-400' : 'text-amber-400'}`} />
        <p className="text-xl font-bold">{formatCurrency(revenue)}</p>
        <p className="text-sm text-muted-foreground">{bucket}</p>
        <p className="text-xs text-muted-foreground mt-1">{formatNumber(customers)} customers</p>
      </CardContent>
    </Card>
  );
};

export default function ExecutiveDashboard() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiveData, setIsLiveData] = useState(false);
  const [filters, setFilters] = useState({
    date_range: 'lifetime',
    city: null,
    store_cluster: 'All',
    customer_type: 'All'
  });
  const [activeTab, setActiveTab] = useState('overview'); // overview, trends, comparison, risk
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [chartZoom, setChartZoom] = useState({ open: false, type: '', data: [], title: '', config: {} });

  // Chart zoom handlers
  const openChartZoom = (chartType, chartData, chartTitle, chartConfig = {}) => {
    setChartZoom({ open: true, type: chartType, data: chartData, title: chartTitle, config: chartConfig });
  };
  const closeChartZoom = () => setChartZoom({ open: false, type: '', data: [], title: '', config: {} });

  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  const fetchData = useCallback(async () => {
    if (!selectedBrand) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Check for live Data Bridge - mark as live if brand has db_brand_id
    const dbBrandId = getDbBrandId();
    
    // Use original API for proper data structure
    // Add cache-busting to ensure fresh data
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      console.log('Fetching executive dashboard for brand:', selectedBrand.brand_id);
      
      const response = await fetch(
        `${API}/api/analytics/executive-dashboard?_t=${Date.now()}&_rid=${requestId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'X-Request-ID': requestId
          },
          credentials: 'include',
          body: JSON.stringify({ 
            brand_id: selectedBrand.brand_id, 
            filters: {
              date_range: filters.date_range,
              city: filters.city,
              store_cluster: filters.store_cluster,
              customer_type: filters.customer_type
            }
          })
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Executive dashboard response received, success:', result.success);
      
      if (result && result.data) {
        // Force new object reference to ensure React re-renders
        setData({...result.data});
        // Set live status from response or from brand settings
        setIsLiveData(result.data?.source === 'live_data_bridge' || !!dbBrandId);
        console.log('Executive dashboard data set successfully');
      } else {
        console.error('Invalid response structure:', result);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Executive dashboard error:', err);
      setError('Failed to load executive dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrillDown = (type, value, title) => {
    setDrillDown({ open: true, type, data: { value }, title });
  };

  const closeDrillDown = () => {
    setDrillDown({ open: false, type: null, data: null, title: '' });
  };

  // Icon mapping for KPIs
  const KPI_ICONS = {
    'pie-chart': PieChartIcon,
    'trending-up': TrendingUp,
    'users': Users,
    'repeat': Repeat,
    'arrow-up-right': ArrowUpRight,
    'wallet': Wallet,
    'refresh-cw': RefreshCw,
    'target': Target
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="executive-dashboard-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <Brain className="w-16 h-16 text-violet-500 animate-pulse" />
            <div className="absolute -top-2 -right-2">
              <Sparkles className="w-6 h-6 text-amber-400 animate-bounce" />
            </div>
          </div>
          <p className="text-muted-foreground">Loading executive insights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const kpis = data?.kpis || [];
  const summary = data?.summary || {};
  const comparison = data?.comparison || {};
  const trends = data?.trends || {};
  const cohortContribution = data?.cohort_contribution || [];
  const revenueAtRisk = data?.revenue_at_risk || [];
  const storeClusters = data?.store_clusters || {};
  const insights = data?.insights || [];
  const filterOptions = data?.filters || {};
  const deltas = data?.deltas || {};

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Header with Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-500 shadow-lg shadow-violet-500/30">
              <Crown className="w-6 h-6 text-white" />
            </div>
            Executive Loyalty Dashboard
            {isLiveData && (
              <Badge className="ml-2 bg-emerald-500 text-white animate-pulse">
                <Activity className="w-3 h-3 mr-1" />
                LIVE
              </Badge>
            )}
            {data?.data_as_of && (
              <Badge className="ml-2 bg-blue-500/80 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Data as of: {data.data_as_of}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground">
            {isLiveData 
              ? `Live data from ${selectedBrand?.name || 'Brand'} - ${(data?.summary?.total_customers || data?.kpis?.total_customers || 0).toLocaleString()} customers`
              : 'Real-time loyalty program performance & insights'
            }
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Range Filter */}
          <Select 
            value={filters.date_range} 
            onValueChange={(value) => setFilters(prev => ({ ...prev, date_range: value }))}
          >
            <SelectTrigger className="w-32">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['lifetime', 'YTD', 'L12M', 'QTD', 'MTD'].map(range => (
                <SelectItem key={range} value={range}>{range === 'lifetime' ? 'Lifetime' : range}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Toggle Advanced Filters */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
          
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          <ExportButton 
            brandId={selectedBrand?.brand_id}
            dashboardType="executive"
            label="Full Report"
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Advanced Filters (Collapsible) */}
      {showFilters && (
        <Card className="animate-in slide-in-from-top-2">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">City</label>
                <Select 
                  value={filters.city || 'all'}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, city: v === 'all' ? null : v }))}
                >
                  <SelectTrigger className="w-full">
                    <MapPin className="w-3 h-3 mr-2" />
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {filterOptions.cities?.slice(0, 20).map(c => (
                      <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Store Cluster</label>
                <Select 
                  value={filters.store_cluster}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, store_cluster: v }))}
                >
                  <SelectTrigger className="w-full">
                    <Store className="w-3 h-3 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filterOptions.store_clusters?.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Customer Type</label>
                <Select 
                  value={filters.customer_type}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, customer_type: v }))}
                >
                  <SelectTrigger className="w-full">
                    <Users className="w-3 h-3 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filterOptions.customer_types?.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Channel</label>
                <Select defaultValue="All">
                  <SelectTrigger className="w-full">
                    <Layers className="w-3 h-3 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filterOptions.channels?.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Mode Tabs */}
      <div className="flex items-center gap-2 bg-muted p-1 rounded-lg w-fit">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'trends', label: 'Trends', icon: Activity },
          { id: 'comparison', label: 'vs Industry Benchmarks', icon: Users },
          { id: 'risk', label: 'Revenue at Risk', icon: AlertTriangle }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            className="gap-2"
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Real GPT-5.2 AI Insight (cached 24h) */}
      <AIInsightPanel
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name || selectedBrand?.brand_name}
        kpiName="executive_dashboard_summary"
        context={{
          kpis: (kpis || []).map(k => ({ id: k.id, label: k.label, value: k.value, formatted: k.formatted, mom_delta: k.mom_delta, yoy_delta: k.yoy_delta })),
          total_revenue: data?.summary?.total_revenue,
          total_customers: data?.summary?.total_customers,
        }}
      />

      {/* KPI Grid - Hero Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {kpis.map((kpi, idx) => {
          const Icon = KPI_ICONS[kpi.icon] || Target;
          return (
            <Card 
              key={idx}
              className="cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all group relative overflow-hidden"
              onClick={() => openDrillDown('kpi', kpi.drilldown, `${kpi.label} Details`)}
            >
              <div 
                className="absolute inset-0 opacity-10" 
                style={{ background: `linear-gradient(135deg, ${kpi.color}20, transparent)` }}
              />
              <CardContent className="p-3 relative">
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-4 h-4" style={{ color: kpi.color }} />
                  <StatusIndicator status={kpi.status} />
                </div>
                <p className="text-xl font-bold mb-0.5">
                  {kpi.formatted === 'N/A' || kpi.value === null ? (
                    <NABadge reason={kpi.na_reason} dataTestId={`exec-kpi-${kpi.id}-na`} />
                  ) : (
                    kpi.formatted
                  )}
                </p>
                <p className="text-xs text-muted-foreground truncate mb-2">
                  <FormulaTooltip metric={kpi.id} name={kpi.label}>
                    <span>{kpi.label}</span>
                  </FormulaTooltip>
                </p>
                <div className="flex items-center gap-2">
                  <DeltaBadge value={kpi.mom_delta} label="MoM" />
                </div>
                <ChevronRight className="w-4 h-4 absolute bottom-2 right-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Content Based on Active Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Revenue Trend Chart */}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-violet-500" />
                  Loyalty vs Non-Loyalty Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trends.revenue || []}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="transactionsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => formatAxisValue(v)} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      name="Total Revenue"
                      stroke="#8B5CF6" 
                      strokeWidth={2}
                      fill="url(#revenueGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cohort Revenue Contribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4 text-pink-500" />
                  Cohort Revenue Contribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={cohortContribution}
                      dataKey="revenue"
                      nameKey="cohort"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      label={({ cohort, revenue_pct }) => `${revenue_pct}%`}
                      labelLine={false}
                      onClick={(data) => openDrillDown('cohort', data.cohort, `Cohort: ${data.cohort}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {cohortContribution.map((entry, index) => (
                        <Cell 
                          key={index} 
                          fill={['#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#3B82F6'][index % 5]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-2">
                  {cohortContribution.slice(0, 4).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: ['#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B'][i] }}
                        />
                        <span className="text-muted-foreground">{c.cohort}</span>
                      </div>
                      <span className="font-medium">{c.revenue_pct}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Insights Row */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              AI-Powered Insights
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {insights.slice(0, 6).map((insight, idx) => (
                <InsightCard 
                  key={idx} 
                  insight={insight}
                  onClick={() => openDrillDown('insight', insight.id, insight.title)}
                />
              ))}
            </div>
          </div>

          {/* Points Liability Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Gem className="w-4 h-4 text-amber-500" />
                Points Liability vs Burn
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={trends.points || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tickFormatter={(v) => `${(v/1000000).toFixed(0)}M`} tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => formatAxisValue(v, true)} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="earned" name="Points Earned" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="burned" name="Points Burned" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="liability_value" name="Liability Value" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'trends' && (
        <>
          {/* Monthly Performance Metrics */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  Customer & Transaction Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={trends.revenue || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="customers" name="Unique Customers" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="bills" name="Transactions" stroke="#EC4899" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-emerald-500" />
                  ATV & Frequency Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trends.revenue || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tickFormatter={(v) => formatAxisValue(v, true)} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="member_atv" name="ATV" stroke="#8B5CF6" strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="member_frequency" name="Frequency" stroke="#10B981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Store Cluster Performance */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Store className="w-4 h-4 text-violet-500" />
                Store Cluster Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {Object.entries(storeClusters).map(([key, cluster]) => (
                  <Card 
                    key={key}
                    className={`cursor-pointer hover:shadow-lg transition-all ${
                      key === 'top' ? 'bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/20' :
                      key === 'mid' ? 'bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border-blue-500/20' :
                      'bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20'
                    }`}
                    onClick={() => openDrillDown('store_cluster', key, cluster.label)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline">{cluster.label}</Badge>
                        <span className="text-xs text-muted-foreground">{cluster.stores} stores</span>
                      </div>
                      <p className="text-2xl font-bold">{formatCurrency(cluster.revenue)}</p>
                      <p className="text-sm text-muted-foreground mt-1">{cluster.customers?.toLocaleString()} customers</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'comparison' && (
        <>
          {/* Performance vs Industry Benchmarks */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-500" />
                Performance vs Industry Benchmarks
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <ComparisonBar 
                label="Average Transaction Value (ATV)"
                currentValue={comparison.atv?.current || comparison.atv?.member || 0}
                benchmarkValue={comparison.atv?.benchmark || comparison.atv?.non_member || 0}
                vsBenchmark={comparison.atv?.vs_benchmark || comparison.atv?.lift || 0}
                benchmarkLabel={comparison.atv?.benchmark_label || "Industry Avg"}
              />
              
              <ComparisonBar 
                label="Visit Frequency (Monthly)"
                currentValue={comparison.frequency?.current || comparison.frequency?.member || 0}
                benchmarkValue={comparison.frequency?.benchmark || comparison.frequency?.non_member || 0}
                vsBenchmark={comparison.frequency?.vs_benchmark || comparison.frequency?.lift || 0}
                benchmarkLabel={comparison.frequency?.benchmark_label || "Industry Avg"}
                format="decimal"
              />
              
              <ComparisonBar 
                label="Annual Customer Value"
                currentValue={comparison.annual_value?.current || comparison.annual_value?.member || 0}
                benchmarkValue={comparison.annual_value?.benchmark || comparison.annual_value?.non_member || 0}
                vsBenchmark={comparison.annual_value?.vs_benchmark || comparison.annual_value?.lift || 0}
                benchmarkLabel={comparison.annual_value?.benchmark_label || "Industry Avg"}
              />
            </CardContent>
          </Card>

          {/* Performance Highlights */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/5 border-violet-500/20">
              <CardContent className="p-6 text-center">
                <Wallet className="w-10 h-10 mx-auto mb-3 text-violet-400" />
                <p className="text-4xl font-bold text-violet-400">{(comparison.atv?.vs_benchmark || comparison.atv?.lift || 0) >= 0 ? '+' : ''}{comparison.atv?.vs_benchmark || comparison.atv?.lift || 0}%</p>
                <p className="text-sm text-muted-foreground mt-2">vs Benchmark ATV</p>
                <p className="text-xs text-muted-foreground mt-1">Transaction value performance</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/20">
              <CardContent className="p-6 text-center">
                <Repeat className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
                <p className="text-4xl font-bold text-emerald-400">{(comparison.frequency?.vs_benchmark || comparison.frequency?.lift || 0) >= 0 ? '+' : ''}{comparison.frequency?.vs_benchmark || comparison.frequency?.lift || 0}%</p>
                <p className="text-sm text-muted-foreground mt-2">vs Benchmark Frequency</p>
                <p className="text-xs text-muted-foreground mt-1">Visit frequency performance</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
              <CardContent className="p-6 text-center">
                <Crown className="w-10 h-10 mx-auto mb-3 text-amber-400" />
                <p className="text-4xl font-bold text-amber-400">{(comparison.annual_value?.vs_benchmark || comparison.annual_value?.lift || 0) >= 0 ? '+' : ''}{comparison.annual_value?.vs_benchmark || comparison.annual_value?.lift || 0}%</p>
                <p className="text-sm text-muted-foreground mt-2">vs Benchmark Value</p>
                <p className="text-xs text-muted-foreground mt-1">Annual customer value</p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {activeTab === 'risk' && (
        <>
          {/* Revenue at Risk Header */}
          <Card className="bg-gradient-to-r from-red-500/10 via-orange-500/5 to-amber-500/10 border-red-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Total Revenue at Risk
                  </h3>
                  <p className="text-3xl font-bold mt-2 text-red-400">
                    {formatCurrency(data?.total_revenue_at_risk || 0)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    From {revenueAtRisk.reduce((sum, r) => sum + r.customers, 0).toLocaleString()} inactive customers
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">As % of current revenue</p>
                  <p className="text-xl font-semibold text-amber-400">
                    {summary.total_revenue > 0 ? ((data?.total_revenue_at_risk / summary.total_revenue) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Risk Buckets */}
          <div className="grid md:grid-cols-3 gap-6">
            {revenueAtRisk.map((risk, idx) => (
              <RiskCard 
                key={idx}
                bucket={risk.bucket}
                revenue={risk.revenue}
                customers={risk.customers}
                riskLevel={risk.risk_level}
              />
            ))}
          </div>

          {/* Risk Insights */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                Win-Back Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {insights.filter(i => i.id === 'revenue_at_risk' || i.id === 'repeat_rate').map((insight, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className={`p-2 rounded-lg ${insight.severity === 'critical' ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
                      {insight.severity === 'critical' ? 
                        <AlertCircle className="w-5 h-5 text-red-400" /> : 
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                      }
                    </div>
                    <div>
                      <h4 className="font-semibold">{insight.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{insight.insight}</p>
                      <p className="text-sm text-primary mt-2">💡 {insight.recommendation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Deep AI Insights - FundleBrain Chief Intelligence Engine */}
      <DeepAIInsights
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name}
        dashboardType="executive"
        dashboardData={data}
        kpis={{
          total_customers: { value: data?.totals?.total_customers || 0 },
          total_revenue: { value: data?.totals?.total_revenue || 0 },
          repeat_rate: { value: data?.totals?.repeat_rate || 0 },
          avg_atv: { value: data?.totals?.avg_atv || 0 },
          loyalty_contribution: { value: data?.totals?.loyalty_contribution || 0 }
        }}
        trends={data?.trends || {}}
      />

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />

      {/* Chart Zoom Modal */}
      <ChartZoomModal
        open={chartZoom.open}
        onClose={closeChartZoom}
        chartType={chartZoom.type}
        chartData={chartZoom.data}
        chartTitle={chartZoom.title}
        chartConfig={chartZoom.config}
        brandId={selectedBrand?.brand_id}
        dashboardType="executive"
      />
    </div>
  );
}
